// One entry's photos, as a strip on the continuation row under it.
//
// Each thumbnail is a real <button>, not PhotoList's `<span onClick>`: this
// sits inside a clickable <tr>, so it has to swallow its own click anyway, and
// a span no keyboard user can reach would be the wrong way to do it.
//
// The picture shown is the entry's PREVIEW where one exists (~50 KB) and the
// original otherwise -- see useEntryPhotos. A strip of originals would be
// several megabytes per leg and tens across a trip, which is the whole reason
// the preview Lambda exists.
//
// `limit` defaults to MAX_ENTRY_PHOTOS, which is every existing caller and the
// behaviour this had before the prop existed. The shared ENTRY page passes
// Infinity: the "+N more" tile's destination is the entry's own page, and on
// that page it would point at itself, so the cap has to be liftable rather than
// worked around.

import AttachmentImage from '../../components/AttachmentImage';
import { isGuestPhotoName, displayPhotoName } from '../../fleet-shared/Attachments.mjs';

// How many photos a leg shows inline. Past this the strip offers the entry
// instead, whose Attached Files card is the complete list with upload and
// delete. Exported so the tests read the same number.
export const MAX_ENTRY_PHOTOS = 6;

export function EntryPhotoStrip({ photos, onOpenPhoto, onOpenEntry, limit = MAX_ENTRY_PHOTOS }) {
  if (!photos || photos.length === 0) {
    return null;
  }

  const shown = photos.slice(0, limit);
  const hidden = photos.length - shown.length;

  return (
    <div className="tp-entry-photos">
      {shown.map((photo) => {
        const display = photo.preview ?? photo;
        // A visitor's upload, added through a shared trip's link. Marked so the
        // owner can tell whose photo they are looking at before deciding to keep
        // it, and so a reader of the shared page knows the trip has contributions.
        // An owner's own file is never guest-named, so every existing caller draws
        // exactly what it drew before.
        const fromVisitor = isGuestPhotoName(photo.name);
        const label = displayPhotoName(photo.name);
        return (
          <button
            key={photo.key}
            type="button"
            className="tp-entry-photo"
            title={fromVisitor ? `${label} (added by a visitor)` : label}
            // MANDATORY. The row opens the entry; without this a tap on a
            // thumbnail would open the photo AND navigate away underneath it.
            onClick={(e) => {
              e.stopPropagation();
              onOpenPhoto(photo);
            }}
          >
            {/* No `onError`: AttachmentImage's inline glyph is the signal, and
                one unreadable photo must not reach the page's status. */}
            <AttachmentImage
              imageKey={display.key}
              // `undefined` for every owner-side photo, where the key is what
              // resolves. The shared page's photos arrive presigned instead,
              // because an anonymous viewer has no Amplify Storage to call.
              src={display.url}
              alt={label}
              sizeBytes={display.size}
            />
            {fromVisitor && (
              // Decoration: the accessible name is on the button's title above, so
              // a screen reader hears "added by a visitor" without this repeating
              // it.
              <span className="tp-entry-photo-guest" aria-hidden="true">
                <i className="fa-regular fa-user" />
              </span>
            )}
          </button>
        );
      })}

      {hidden > 0 && (
        // Dashed, so it reads as "not a photo". It opens the entry -- the same
        // place the row click goes, so there is no second navigation rule to
        // keep in step -- and stops the click anyway, so the behaviour is
        // stated rather than inherited.
        <button
          type="button"
          className="tp-entry-photo-more"
          onClick={(e) => {
            e.stopPropagation();
            onOpenEntry();
          }}
        >
          +{hidden} more
        </button>
      )}
    </div>
  );
}

export default EntryPhotoStrip;
